import { getPageSizeCss } from '../pageSettings';
import type { PageSettings } from '../pageSettings';
import type { PageProfile } from './types';

interface PageProfileRuntime {
    getOrCreatePageProfile(pageSettings: PageSettings): PageProfile;
    getPageProfiles(): readonly PageProfile[];
}

function getPageDimensionsFromSizeCss(pageSizeCss: string): { width: string; height: string } {
    const [width, height] = pageSizeCss.split(' ');

    if (!width || !height) {
        throw new Error(`Invalid page size css value "${pageSizeCss}".`);
    }

    return { width, height };
}

export function createPageProfileRuntime(pageSettings: PageSettings): PageProfileRuntime {
    const pageProfilesByKey = new Map<string, PageProfile>();

    function getOrCreatePageProfile(profileSettings: PageSettings): PageProfile {
        const { pageSize, pageOrientation, pageMargin } = profileSettings;
        const normalizedProfileSettings: PageSettings = {
            pageSize,
            pageOrientation,
            pageMargin: { value: pageMargin.value, unit: pageMargin.unit },
        };
        const profileKey = JSON.stringify(normalizedProfileSettings);
        const existingProfile = pageProfilesByKey.get(profileKey);
        if (existingProfile) {
            return existingProfile;
        }

        const profileIndex = pageProfilesByKey.size;
        const sizeCss = getPageSizeCss(pageSize, pageOrientation);
        const { width, height } = getPageDimensionsFromSizeCss(sizeCss);
        const profile: PageProfile = {
            ...normalizedProfileSettings,
            pageName: `rb_page_profile_${profileIndex}`,
            pageClassName: `rb-page-profile-${profileIndex}`,
            sizeCss,
            width,
            height,
        };

        pageProfilesByKey.set(profileKey, profile);
        return profile;
    }

    function getPageProfiles(): readonly PageProfile[] {
        return Array.from(pageProfilesByKey.values());
    }

    getOrCreatePageProfile(pageSettings);

    return {
        getOrCreatePageProfile,
        getPageProfiles,
    };
}
